import { useParams, Link } from 'react-router-dom'
import { roles, industries, experienceLevels, aiUsageLevels } from '../data/roles.ts'
import { calculateSalaryGap, formatCurrency } from '../utils/calculations.ts'
import { useMemo } from 'react'
import SeoH1 from '../components/SeoH1.tsx'

export default function RolePage() {
  const { roleId } = useParams<{ roleId: string }>()
  const role = useMemo(() => roles.find(r => r.id === roleId) || roles[0], [roleId])

  const result = useMemo(() => calculateSalaryGap(role, {
    roleId: role.id,
    industryId: industries[0].id,
    experienceId: experienceLevels[1].id,
    currentSalary: role.baseSalary,
    aiUsageId: aiUsageLevels[0].id,
    years: 5
  }), [role])

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="mb-8">
        <SeoH1 fallback={`${role.title} AI Salary Gap`} className="text-3xl font-bold text-slate-900 mb-3" />
        <p className="text-slate-600 max-w-2xl">
          {role.title}s in the {role.category} category have a {role.aiExposureTier} AI exposure tier.
          At a median salary of {formatCurrency(role.baseSalary)}, an AI-upskilled peer could widen their income by
          {formatCurrency(result.year5Gap)} by year 5.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="card p-4">
          <div className="metric-label">AI exposure tier</div>
          <div className="metric-value">{role.aiExposureTier}</div>
        </div>
        <div className="card p-4">
          <div className="metric-label">Base AI premium</div>
          <div className="metric-value">{(role.basePremium * 100).toFixed(0)}%</div>
        </div>
        <div className="card p-4">
          <div className="metric-label">Year 5 gap</div>
          <div className="metric-value text-emerald-600">{formatCurrency(result.year5Gap)}</div>
        </div>
        <div className="card p-4">
          <div className="metric-label">5-year cumulative gap</div>
          <div className="metric-value text-emerald-600">{formatCurrency(result.cumulativeGap)}</div>
        </div>
      </div>

      <div className="card p-6 mb-8">
        <h2 className="text-lg font-semibold text-slate-900 mb-3">Top skills that close the gap</h2>
        <div className="flex flex-wrap gap-2">
          {role.topSkills.map((skill, i) => (
            <span key={i} className="px-3 py-1 rounded-full bg-primary-50 text-primary-700 text-sm font-medium">
              {skill}
            </span>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-4">
        <Link to={`/ai-salary-gap?role=${role.id}`} className="btn-primary">
          Customize for your salary
        </Link>
        <Link to={`/skills-roi?role=${role.id}`} className="btn-secondary">
          Compare skill ROI
        </Link>
      </div>
    </div>
  )
}
